// TwistedTorus shader based on Smooth Voronoi Contours
// Creates smooth contour patterns using 2D Voronoi with gradient-based smoothing
const frag = `
precision mediump float;

uniform vec2 u_resolution;
uniform float u_time;
uniform float u_speed;
uniform float u_frequency;
uniform float u_smoothness;
uniform float u_falloff;
uniform float u_layers;
uniform float u_seed;

uniform vec3 u_color_0;
uniform vec3 u_color_1;
uniform vec3 u_color_2;
uniform vec3 u_color_3;
uniform vec3 u_color_4;
uniform vec3 u_color_5;

varying vec2 vUv;

#define PI 3.14159265359

// Standard 2x2 hash algorithm with time and seed
vec2 hash22(vec2 p) {
    float n = sin(dot(p, vec2(41, 289)) + u_seed);
    p = fract(vec2(2097152, 262144) * n);
    return cos(p * 6.283 + u_time * u_speed) * 0.5;
}

// Smooth Voronoi implementation
float smoothVoronoi(vec2 p, float falloff) {
    vec2 ip = floor(p); 
    p -= ip;
    
    float d = 1.0, res = 0.0;
    
    for(int i = -1; i <= 2; i++) {
        for(int j = -1; j <= 2; j++) {
            vec2 b = vec2(i, j);
            vec2 v = b - p + hash22(ip + b);
            d = max(dot(v, v), 1e-4);
            res += 1.0 / pow(d, falloff);
        }
    }
    
    return pow(1.0 / res, 0.5 / falloff);
}

// 2D function for contour generation
float func2D(vec2 p) {
    float d = smoothVoronoi(p * 2.0, u_falloff) * 0.66 + 
              smoothVoronoi(p * 6.0, u_falloff) * 0.34;
    return sqrt(d);
}

// Smooth fract function for anti-aliased contours
float smoothFract(float x, float sf) {
    x = fract(x); 
    return min(x, x * (1.0 - x) * sf);
}

// Color lookup table using the 6 color palette
vec3 lut(float x) {
    if (x < 0.0 || x > 1.0) return u_color_0;

    float P1 = abs(x - 0.0);
    float P2 = abs(x - 0.2);
    float P3 = abs(x - 0.4);
    float P4 = abs(x - 0.6);
    float P5 = abs(x - 0.8);
    float P6 = abs(x - 1.0);

    float expand = 5.0;
    float R1 = smoothstep(1.0, 0.0, P1 * expand);
    float R2 = smoothstep(1.0, 0.0, P2 * expand);
    float R3 = smoothstep(1.0, 0.0, P3 * expand);
    float R4 = smoothstep(1.0, 0.0, P4 * expand);
    float R5 = smoothstep(1.0, 0.0, P5 * expand);
    float R6 = smoothstep(1.0, 0.0, P6 * expand);

    vec3 color = u_color_0 * R1 +
                 u_color_1 * R2 +
                 u_color_2 * R3 +
                 u_color_3 * R4 +
                 u_color_4 * R5 +
                 u_color_5 * R6;
    return color;
}

void main() {
    // Screen coordinates
    vec2 uv = (gl_FragCoord.xy - u_resolution.xy * 0.5) / u_resolution.y;
    
    // Standard epsilon for numerical gradient
    vec2 e = vec2(0.001, 0);
    
    // Function value
    float f = func2D(uv);
    
    // Numerical gradient length
    float g = length(vec2(f - func2D(uv - e.xy), f - func2D(uv - e.yx))) / e.x;
    g = 1.0 / max(g, 0.001);
    
    // Smooth contours with adjustable frequency
    float freq = u_frequency;
    float smoothFactor = u_resolution.y * u_smoothness;
    
    // Create contours using cosine waves
    float c = clamp(cos(f * freq * PI * 2.0) * g * smoothFactor, 0.0, 1.0);
    
    // Layer mixing
    vec3 baseColor = lut(c);
    vec3 accentColor = lut(c * 0.5 + 0.5);
    
    // Mix colors based on function value and layers
    vec3 col = mix(baseColor, accentColor, -uv.y + clamp(cos(f * freq * PI) * 2.0, 0.0, 1.0));
    
    // Highlight specific contour bands
    f = f * freq;
    if(f > 8.5 && f < 9.5) {
        col *= vec3(1.0, 0.2, 0.1);
    }
    if(f > 4.5 && f < 5.5) {
        col *= vec3(0.1, 1.0, 0.2);
    }
    
    // Add subtle gradient highlighting
    col += g * g * g * vec3(0.3, 0.5, 1.0) * 0.025;
    
    gl_FragColor = vec4(sqrt(clamp(col, 0.0, 1.0)), 1.0);
}
`;

const shader = {
  name: 'CosmicRipples',
  shader: {
    frag,
  },
  schema: [
    {
      key: 'colors',
      type: 'color-palette',
      label: 'Colors',
      min: 2,
      max: 6,
      default: ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7', '#dda0dd'],
    },
    {
      key: 'u_frequency',
      type: 'number',
      label: 'Frequency',
      min: 1,
      max: 20,
      step: 0.5,
      default: 12,
    },
    {
      key: 'u_smoothness',
      type: 'number',
      label: 'Smoothness',
      min: 0.001,
      max: 0.05,
      step: 0.001,
      default: 0.0125,
    },
    {
      key: 'u_falloff',
      type: 'number',
      label: 'Falloff',
      min: 1,
      max: 8,
      step: 0.1,
      default: 4,
    },
    { key: 'u_layers', type: 'number', label: 'Layers', min: 1, max: 5, step: 0.1, default: 2 },
    { key: 'speed', type: 'number', label: 'Speed', min: 0, max: 5, step: 0.1, default: 1 },
    { key: 'animated', type: 'boolean', label: 'Animated', default: true },
  ],
  examples: [
    'dynamic-shader(CosmicRipples, colors=#ff6b6b,#4ecdc4,#45b7d1; u_frequency=12; u_smoothness=0.0125; animated=true)',
    'dynamic-shader(CosmicRipples, colors=#e17055,#81ecec,#fab1a0; u_frequency=8; u_smoothness=0.02; animated=false)',
    'dynamic-shader(CosmicRipples, colors=#6c5ce7,#a29bfe,#fd79a8; u_frequency=15; u_smoothness=0.008; animated=true)',
  ],
};

export default shader;
